import React, { useState } from 'react';
import { Button, Modal, Form, Input, Select, Checkbox, List, Avatar, Space, Popconfirm, notification } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, PhoneOutlined } from '@ant-design/icons';

const { Option } = Select;

const pakistanCities = [
    { name: 'Karachi' },
    { name: 'Lahore' },
    { name: 'Islamabad' },
    { name: 'Rawalpindi' },
    { name: 'Peshawar' },
    { name: 'Quetta' },
    { name: 'Multan' },
    // Add more cities as needed
];

const ProfilePage = () => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [addresses, setAddresses] = useState([
        { name: 'Muhammad Ali', addressLine1: '123 Jinnah Road', addressLine2: 'Saddar', phone: '0300-1234567', city: 'Karachi', state: 'Sindh', pinCode: '74000', isDefault: true },
        { name: 'Fatima Khan', addressLine1: '456 Mall Road', addressLine2: 'Gulberg', phone: '0321-9876543', city: 'Lahore', state: 'Punjab', pinCode: '54000', isDefault: false },
    ]);
    const [editingIndex, setEditingIndex] = useState(-1);

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingIndex(-1);
    };

    const onFinish = (values) => {
        if (editingIndex >= 0) {
            const newAddresses = addresses.map((addr, index) => {
                if (index === editingIndex) {
                    return { ...values, isDefault: values.isDefault || addr.isDefault };
                }
                return { ...addr, isDefault: addr.isDefault && !values.isDefault };
            });
            setAddresses(newAddresses);
            notification.success({ message: 'Address updated successfully!' });
        } else {
            if (values.isDefault) {
                setAddresses(addresses.map(addr => ({ ...addr, isDefault: false })).concat({ ...values, isDefault: true }));
            } else {
                setAddresses([...addresses, values]);
            }
            notification.success({ message: 'Address added successfully!' });
        }
        setIsModalVisible(false);
        form.resetFields();
        setEditingIndex(-1);
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        form.setFieldsValue(addresses[index]);
        setIsModalVisible(true);
    };

    const handleDelete = (index) => {
        const newAddresses = addresses.filter((_, i) => i !== index);
        if (addresses[index].isDefault && newAddresses.length > 0) {
            newAddresses[0].isDefault = true;
        }
        setAddresses(newAddresses);
        notification.success({ message: 'Address deleted successfully!' });
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4">My Profile</h2>
            <Button type="primary" icon={<PlusOutlined />} onClick={showModal} className="mb-4">
                Add New Address
            </Button>
            <List
                itemLayout="vertical"
                dataSource={addresses}
                renderItem={(item, index) => (
                    <List.Item className={`${item.isDefault ? 'border border-primary' : 'border'} rounded p-3 mb-3`}>
                        <List.Item.Meta
                            avatar={<Avatar icon={<EnvironmentOutlined />} style={{ backgroundColor: item.isDefault ? '#1890ff' : '#ccc' }} />}
                            title={<span className="font-weight-bold">{item.name} {item.isDefault && <small className="text-primary">(Default)</small>}</span>}
                            description={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <div>{item.addressLine1}, {item.addressLine2}</div>
                                        <div>{item.city}, {item.state} - {item.pinCode}</div>
                                        <div><PhoneOutlined /> {item.phone}</div>
                                    </div>
                                    <Space>
                                        <Button icon={<EditOutlined />} onClick={() => handleEdit(index)}>Edit</Button>
                                        <Popconfirm
                                            title="Are you sure to delete this address?"
                                            onConfirm={() => handleDelete(index)}
                                            okText="Yes"
                                            cancelText="No"
                                            disabled={item.isDefault}
                                        >
                                            <Button icon={<DeleteOutlined />} danger disabled={item.isDefault}>Delete</Button>
                                        </Popconfirm>
                                    </Space>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
            <Modal
                title={editingIndex >= 0 ? "Edit Address" : "Add a New Address"}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter your name' }]}>
                        <Input placeholder="Enter Name" />
                    </Form.Item>
                    <Form.Item name="phone" label="Mobile Number" rules={[{ required: true, message: 'Please enter your mobile number' }]}>
                        <Input placeholder="Enter Mobile Number" />
                    </Form.Item>
                    <Form.Item name="addressLine1" label="House no., Building, Street" rules={[{ required: true, message: 'Please enter Address Line 1' }]}>
                        <Input placeholder="Address Line 1" />
                    </Form.Item>
                    <Form.Item name="addressLine2" label="Area, Sector">
                        <Input placeholder="Address Line 2" />
                    </Form.Item>
                    <Form.Item name="city" label="City" rules={[{ required: true, message: 'Please select a city' }]}>
                        <Select placeholder="Select City" showSearch>
                            {pakistanCities.map(city => (
                                <Option key={city.name} value={city.name}>{city.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="pinCode" label="Postal Code" rules={[{ required: true, message: 'Please enter your postal code' }]}>
                        <Input placeholder="Enter Postal Code" />
                    </Form.Item>
                    <Form.Item name="state" label="Province" rules={[{ required: true, message: 'Please select a province' }]}>
                        <Select placeholder="Select Province">
                            <Option value="Punjab">Punjab</Option>
                            <Option value="Sindh">Sindh</Option>
                            <Option value="Khyber Pakhtunkhwa">Khyber Pakhtunkhwa</Option>
                            <Option value="Balochistan">Balochistan</Option>
                            <Option value="Gilgit-Baltistan">Gilgit-Baltistan</Option>
                            <Option value="Azad Kashmir">Azad Kashmir</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="isDefault" valuePropName="checked">
                        <Checkbox>Use as my default address</Checkbox>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingIndex >= 0 ? 'Update Address' : 'Add New Address'}
                            </Button>
                            <Button onClick={handleCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProfilePage;